/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useCallback, useEffect, useState } from 'react';
import { withStepProps } from 'octa-front-sdk/dist/components/Wizard/Step';
import { GoogleMap, Marker } from '@react-google-maps/api';
import GooglePlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
  geocodeByLatLng,
} from 'react-google-places-autocomplete';

import { FormatGoogleMapsResponseIntoAddress } from '../../../services/FormatGoogleMapsResponseIntoAddress';

import { Address } from '../../../types/Address';

import BtnWithLoad from '../../BtnWithLoad';
import AddressForm from './AddressForm';

import TargetGps from '../../../images/icons/crosshairs-gps.svg';

import {
  Container,
  MapContainer,
  SearchContainer,
  BtnContainer,
  UserLocationBtn,
} from './styles';

const initialPosition = { lat: -14.206914, lng: -51.915471 };

interface Coords {
  lat: number;
  lng: number;
}
interface IProps {
  address?: Address;
  onSave?: (newAddress?: Address) => void;
  onRemove?: () => void;
  shouldGoBackOnSave?: boolean;
}

const AddressStep: React.FC<IProps> = props => {
  const { address, shouldGoBackOnSave, onRemove, onSave } = props;
  const [autocompleteValue, setAutocompleteValue] = useState();
  const [addressFound, setAddressFound] = useState<Address | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [zoom, setZoom] = useState(5);
  const [location, setLocation] = useState<Coords>(() => {
    if (address?.location) {
      return {
        lat: address?.location?.latitude,
        lng: address?.location?.longitude,
      };
    }
    return initialPosition;
  });

  const handleMapSelectionSubmit = useCallback(async () => {
    if (location === null) return alert('Selecione uma localidade');

    if (addressFound) {
      setShowForm(true);
    } else {
      alert('Erro ao buscar endereço');
    }
  }, [addressFound, location]);

  const getUserLocation = useCallback(() => {
    if (!process.env.REACT_APP_IS_APPLE && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p): void => {
          const { latitude, longitude } = p.coords;

          const userCoords = {
            lng: Number(longitude.toFixed(7)),
            lat: Number(latitude.toFixed(7)),
          };

          setLocation(userCoords);

          geocodeByLatLng(userCoords)
            .then(result => FormatGoogleMapsResponseIntoAddress(result[0]))
            .then(address => {
              setZoom(18);
              setAddressFound(address);
            });
        },
        () => alert('Error ao buscar sua localização'),
        {
          enableHighAccuracy: true,
          timeout: 5000,
        },
      );
    } else {
      alert('Sem permissão para buscar localização do usuário');
    }
  }, []);

  useEffect(() => {
    if (autocompleteValue) {
      // @ts-ignore
      geocodeByAddress(autocompleteValue?.value.description)
        .then(result => FormatGoogleMapsResponseIntoAddress(result[0]))
        .then(address => {
          const coordsFound = {
            lat: address.location!.latitude,
            lng: address.location!.longitude,
          };

          setZoom(18);
          setAddressFound(address);
          setLocation(coordsFound);
        });
    }
  }, [autocompleteValue]);

  function handleMapClick(e: google.maps.MapMouseEvent) {
    const coords = {
      lat: e.latLng!.lat(),
      lng: e.latLng!.lng(),
    };

    setLocation(coords);
    geocodeByLatLng(coords)
      .then(result => FormatGoogleMapsResponseIntoAddress(result[0]))
      .then(address => {
        setZoom(18);
        setAddressFound(address);
      });
  }

  return (
    <Container>
      {showForm && addressFound && (
        <AddressForm
          currentAddress={addressFound || address}
          shouldGoBackOnSave={shouldGoBackOnSave}
          setShowForm={setShowForm}
          onSave={onSave}
          onRemove={onRemove}
          isEditing={!!address}
        />
      )}

      {!showForm && (
        <>
          <MapContainer>
            <SearchContainer>
              <GooglePlacesAutocomplete
                selectProps={{
                  autocompleteValue,
                  onChange: setAutocompleteValue,
                  placeholder: 'Busque por CEP ou digite o endereço',
                }}
                apiOptions={{
                  language: 'pt-BR',
                }}
                autocompletionRequest={{
                  componentRestrictions: {
                    country: 'BR',
                  },
                }}
                debounce={1000}
                apiKey={process.env.REACT_APP_MAPS_API_KEY}
                withSessionToken
              />
            </SearchContainer>

            <div className="maps">
              <GoogleMap
                mapContainerStyle={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                }}
                clickableIcons={false}
                options={{
                  streetViewControl: false,
                  fullscreenControl: false,
                  mapTypeControl: false,
                }}
                center={location}
                zoom={zoom}
                onClick={handleMapClick}
              >
                {addressFound && <Marker position={location} />}
              </GoogleMap>
            </div>

            <BtnContainer>
              {!process.env.REACT_APP_IS_APPLE && (
                <UserLocationBtn type="button" onClick={getUserLocation}>
                  <img src={TargetGps} alt="Localização" />
                  Usar minha localização
                </UserLocationBtn>
              )}
              <BtnWithLoad
                name="Avançar"
                className="next-btn"
                onClick={handleMapSelectionSubmit}
                disabled={!location}
              />
            </BtnContainer>
          </MapContainer>
        </>
      )}
    </Container>
  );
};

export default withStepProps(AddressStep);
